import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { History, FileText, Package, Book, ClipboardList } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { RECIPIENT_TYPE_LABEL, money, fmtDate } from '@/lib/accountingConstants';
import EmptyState from '@/components/accounting/EmptyState';
import TableSkeleton from '@/components/accounting/TableSkeleton';

const KIND_META = {
  statement:  { label: 'Statement',  icon: FileText,      tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  batch:      { label: 'Batch',      icon: Package,       tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  ledger:     { label: 'Ledger',     icon: Book,          tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  adjustment: { label: 'Adjustment', icon: ClipboardList, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
};

const KIND_ROUTES = {
  statement:  (r) => `/accounting/statements/${r.id}`,
  batch:      (r) => `/accounting/payment-batches/${r.id}`,
  adjustment: (r) => `/accounting/manual-adjustments?highlight=${r.id}`,
};

export default function AccountingHistoryPage() {
  const navigate = useNavigate();
  const urlParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    kinds: ['statement', 'batch', 'ledger', 'adjustment'],
    recipient_type: urlParams.get('recipient_type') || '',
    recipient_id: urlParams.get('recipient_id') || '',
    date_from: '', date_to: '', created_by: '',
  });

  const toggleKind = (k) => {
    setFilters((f) => {
      const has = f.kinds.includes(k);
      return { ...f, kinds: has ? f.kinds.filter((x) => x !== k) : [...f.kinds, k] };
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.kinds.length) params.kinds = filters.kinds.join(',');
      if (filters.recipient_type) params.recipient_type = filters.recipient_type;
      if (filters.recipient_id)   params.recipient_id = filters.recipient_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to)   params.date_to = filters.date_to;
      if (filters.created_by) params.created_by = filters.created_by;
      const { data } = await accountingAPI.accountingHistory(params);
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load history'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="accounting-history-page">
      <PageHeader
        title="Accounting History"
        description="Unified feed of every financial event — statements, batches, ledger entries and manual adjustments."
        breadcrumbs={[{ label: 'Accounting', to: '/accounting' }, { label: 'History' }]}
      />

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-wide text-slate-500 mr-2">Kinds:</span>
            {Object.entries(KIND_META).map(([k, m]) => {
              const Icon = m.icon;
              const active = filters.kinds.includes(k);
              return (
                <button key={k} onClick={() => toggleKind(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1 ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                  data-testid={`kind-toggle-${k}`}>
                  <Icon className="h-3 w-3" /> {m.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Recipient Type</Label>
              <Select value={filters.recipient_type || 'any'} onValueChange={(v) => setFilters({ ...filters, recipient_type: v === 'any' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="subagency">Subagency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Recipient ID</Label>
              <Input value={filters.recipient_id} onChange={(e) => setFilters({ ...filters, recipient_id: e.target.value })} />
            </div>
            <div>
              <Label>Date From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <Label>Date To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div>
              <Label>Created By (name)</Label>
              <Input value={filters.created_by} onChange={(e) => setFilters({ ...filters, created_by: e.target.value })} placeholder="filter by user name" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} cols={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={History}
              title="No Events Found"
              description="No accounting events match your filters. Adjust the date range or kind toggles to widen the search."
              dataTestId="history-empty-state"
            />
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Kind</th>
                    <th className="text-left px-3 py-2 font-medium">Reference</th>
                    <th className="text-left px-3 py-2 font-medium">Recipient</th>
                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Description</th>
                    <th className="text-left px-3 py-2 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = KIND_META[r.kind] || KIND_META.ledger;
                    const Icon = meta.icon;
                    const route = KIND_ROUTES[r.kind]?.(r);
                    return (
                      <tr key={`${r.kind}-${r.id}`}
                          className={`border-b border-slate-100 hover:bg-slate-50 ${route ? 'cursor-pointer' : ''}`}
                          onClick={() => route && navigate(route)}
                          data-testid={`history-row-${r.id}`}>
                        <td className="px-3 py-2 text-xs text-slate-700">{fmtDate(r.created_at)}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${meta.tone} border font-normal`}>
                            <Icon className="h-3 w-3 mr-1 inline" /> {meta.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.reference || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="text-slate-800">{r.recipient_name || r.recipient_id || '—'}</div>
                          {r.recipient_type && <div className="text-[10px] uppercase text-slate-500">{RECIPIENT_TYPE_LABEL[r.recipient_type] || r.recipient_type}</div>}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${(r.amount || 0) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{money(r.amount)}</td>
                        <td className="px-3 py-2 text-xs uppercase text-slate-600">{r.status || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-700">{r.description || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.created_by_name || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
